// pages/ProductDetailsPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
  getProductById, 
  deactivateProduct,
  activateProduct 
} from '../services/productService';
import { getIcon } from '../utils/icons';

/**
 * Страница просмотра информации о товаре
 * @returns {JSX.Element} - Компонент для рендеринга
 */
const ProductDetailsPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  
  // Загрузка данных о товаре при монтировании компонента
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const productData = await getProductById(id);
        setProduct(productData);
      } catch (err) {
        console.error('Ошибка при загрузке товара:', err);
        toast.error('Не удалось загрузить информацию о товаре');
      } finally {
        setLoading(false);
      }
    };
    
    fetchProduct();
  }, [id]);
  
  /**
   * Переход к редактированию товара
   */
  const handleEdit = () => {
    navigate(`/products/${id}/edit`);
  };
  
  /**
   * Деактивация товара
   */
  const handleDeactivate = async () => {
    if (window.confirm('Вы уверены, что хотите деактивировать этот товар?')) {
      try {
        setProcessing(true);
        await deactivateProduct(id);
        toast.success('Товар успешно деактивирован');
        
        // Обновляем информацию о товаре после деактивации
        const updatedProduct = await getProductById(id);
        setProduct(updatedProduct);
      } catch (err) {
        console.error('Ошибка при деактивации товара:', err);
        toast.error('Не удалось деактивировать товар');
      } finally {
        setProcessing(false);
      }
    }
  };
  
  /**
   * Активация товара
   */
  const handleActivate = async () => {
    if (window.confirm('Вы уверены, что хотите активировать этот товар?')) {
      try {
        setProcessing(true);
        
        // Для активации требуются все текущие данные товара
        await activateProduct(id, {
          name: product.name,
          description: product.description || '',
          barcode: product.barcode || '',
          sku: product.sku || '',
          categoryId: product.categoryId,
          manufacturerId: product.manufacturerId,
          weight: product.weight,
          volume: product.volume,
          dimensions: product.dimensions || ''
        });
        
        toast.success('Товар успешно активирован');
        
        // Обновляем информацию о товаре после активации
        const updatedProduct = await getProductById(id);
        setProduct(updatedProduct);
      } catch (err) {
        console.error('Ошибка при активации товара:', err);
        toast.error('Не удалось активировать товар');
      } finally {
        setProcessing(false);
      }
    }
  };
  
  /**
   * Возврат к списку товаров
   */
  const handleBack = () => {
    navigate(-1);
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  if (!product) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="text-3xl text-red-500 mb-4">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-gray-800 mb-2">Товар не найден</h2>
        <p className="text-gray-600 mb-4">Запрашиваемый товар не существует или был удален</p>
        <button
          onClick={handleBack}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
        >
          Вернуться к списку товаров
        </button>
      </div>
    );
  }
  
  return (
    <div>
      <header className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between">
        <div>
          <div className="flex items-center">
            <button
              onClick={handleBack}
              className="mr-2 text-gray-600 hover:text-gray-800"
              title="Назад к списку"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="text-2xl font-bold text-gray-800">{product.name}</h1>
          </div>
          <p className="text-gray-600 mt-1">Информация о товаре</p>
        </div>
        
        <div className="mt-4 md:mt-0 flex space-x-3">
          <button
            onClick={handleEdit}
            className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md flex items-center"
            disabled={processing}
          >
            {getIcon('edit')}
            <span className="ml-2">Редактировать</span>
          </button>
          
          {product.isActive ? (
            <button
              onClick={handleDeactivate}
              className="bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded-md flex items-center"
              disabled={processing}
            >
              {processing ? (
                <span className="animate-spin h-5 w-5 mr-2 border-t-2 border-b-2 border-white rounded-full"></span>
              ) : (
                getIcon('block')
              )}
              <span className="ml-2">Деактивировать</span>
            </button>
          ) : (
            <button
              onClick={handleActivate}
              className="bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-md flex items-center"
              disabled={processing}
            >
              {processing ? (
                <span className="animate-spin h-5 w-5 mr-2 border-t-2 border-b-2 border-white rounded-full"></span>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
              <span className="ml-2">Активировать</span>
            </button>
          )}
        </div>
      </header>
      
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="p-5 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-800">Общая информация</h2>
            <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
              product.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}>
              {product.isActive ? 'Активен' : 'Неактивен'}
            </span>
          </div>
        </div>
        
        <div className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-medium text-gray-500">Название</h3>
                <p className="mt-1 text-sm text-gray-900">{product.name}</p>
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-gray-500">Категория</h3>
                <p className="mt-1 text-sm text-gray-900">
                  {product.category?.name || 'Не указана'}
                </p>
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-gray-500">Производитель</h3>
                <p className="mt-1 text-sm text-gray-900">
                  {product.manufacturer?.name || 'Не указан'}
                </p>
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-gray-500">Описание</h3>
                <p className="mt-1 text-sm text-gray-900 whitespace-pre-line">
                  {product.description || 'Отсутствует'}
                </p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-medium text-gray-500">Штрихкод</h3>
                <p className="mt-1 text-sm text-gray-900">{product.barcode || 'Не указан'}</p>
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-gray-500">Артикул</h3>
                <p className="mt-1 text-sm text-gray-900">{product.sku || 'Не указан'}</p>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Вес</h3>
                  <p className="mt-1 text-sm text-gray-900">
                    {product.weight ? `${product.weight} кг` : 'Не указан'}
                  </p>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Объем</h3>
                  <p className="mt-1 text-sm text-gray-900">
                    {product.volume ? `${product.volume} л` : 'Не указан'}
                  </p>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Размеры</h3>
                  <p className="mt-1 text-sm text-gray-900">
                    {product.dimensions || 'Не указаны'}
                  </p>
                </div>
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-gray-500">Дата создания</h3>
                <p className="mt-1 text-sm text-gray-900">
                  {new Date(product.createdAt).toLocaleString()}
                </p>
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-gray-500">Последнее обновление</h3>
                <p className="mt-1 text-sm text-gray-900">
                  {new Date(product.updatedAt).toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Здесь можно добавить дополнительные блоки, например, историю цен */}
      
    </div>
  );
};

export default ProductDetailsPage;